import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC, useState } from 'react';

import { Aircraft } from 'domain/entities/Aircraft';
import { Entity } from 'domain/types/Entity';
import { useCaseSearchAircraft } from 'application/aircraft/useCases/useCaseSearchAircraft';
import { useCaseSetAircraftActive } from 'application/aircraft/useCases/useCaseSetAircraftActive';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { Modal } from 'targets/web/components';
import useSnackbar from 'targets/web/modules/dashboard/hooks/useSnackbar';

interface AircraftViewModalProps {
  aircraft: Entity<Aircraft>;
  onClose: () => void;
  open: boolean;
}

const DuplicatesPanel: FC<{ aircraft: Entity<Aircraft> }> = ({ aircraft }) => {
  const t = useTranslationPrefix('configuration.aircraft.modal');
  const tSnackbar = useTranslationPrefix('configuration.aircraft.snackbar');
  const notify = useSnackbar();
  const { setAircraftActive } = useCaseSetAircraftActive();
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const { items = [], isLoading } = useCaseSearchAircraft({ phrase: aircraft.code });

  const duplicates = items.filter((a) => a.code === aircraft.code && a.id !== aircraft.id);

  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  if (!duplicates.length) {
    return null;
  }

  const handleToggle = async (dup: Entity<Aircraft>) => {
    setTogglingId(dup.id);
    try {
      await setAircraftActive(dup.id, !dup.isActive);
      notify(tSnackbar('toggle_success'), { variant: 'success' });
    } catch {
      notify(tSnackbar('toggle_error'), { variant: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <Box mt={4}>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="subtitle2" mb={2}>
        {t('duplicate_section')}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('label_type')}</TableCell>
            <TableCell>{t('label_reg')}</TableCell>
            <TableCell>{t('label_serial')}</TableCell>
            <TableCell>{t('col_status')}</TableCell>
            <TableCell>{t('col_action')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {duplicates.map((dup) => (
            <TableRow key={dup.id}>
              <TableCell>{dup.type.name}</TableCell>
              <TableCell>{dup.code}</TableCell>
              <TableCell>{dup.serialNumber ?? '—'}</TableCell>
              <TableCell>
                <Typography variant="body2" color={dup.isActive ? 'success.main' : 'text.disabled'}>
                  {dup.isActive ? t('status_active') : t('status_inactive')}
                </Typography>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={togglingId === dup.id}
                  onClick={() => handleToggle(dup)}
                >
                  {dup.isActive ? t('btn_deactivate') : t('btn_activate')}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export const AircraftViewModal: FC<AircraftViewModalProps> = ({ aircraft, onClose, open }) => {
  const t = useTranslationPrefix('configuration.aircraft.modal');
  const tSnackbar = useTranslationPrefix('configuration.aircraft.snackbar');
  const notify = useSnackbar();
  const { setAircraftActive } = useCaseSetAircraftActive();
  const [isToggling, setIsToggling] = useState(false);

  const handleToggleActive = async () => {
    setIsToggling(true);
    try {
      await setAircraftActive(aircraft.id, !aircraft.isActive);
      notify(tSnackbar('toggle_success'), { variant: 'success' });
      onClose();
    } catch {
      notify(tSnackbar('toggle_error'), { variant: 'error' });
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('view_title')}
      maxWidth="sm"
      fullWidth
      content={
        <Box>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {t('label_type')}
              </Typography>
              <Typography variant="body1">{aircraft.type.name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {t('label_reg')}
              </Typography>
              <Typography variant="body1">{aircraft.code}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {t('label_serial')}
              </Typography>
              <Typography variant="body1">{aircraft.serialNumber ?? '—'}</Typography>
            </Grid>
          </Grid>

          <DuplicatesPanel aircraft={aircraft} />
        </Box>
      }
      actions={{
        primary: {
          text: aircraft.isActive ? t('btn_deactivate') : t('btn_activate'),
          disabled: isToggling,
          onClick: handleToggleActive,
        },
        secondary: {
          text: 'Close',
        },
      }}
    />
  );
};
